import React, { useRef, useState } from "react";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import { Badge, Button, Copy01, DotsVertical, EmptyState, File05, Menu, PageHeader, Plus, Select, Table, Trash01 } from "@opendate-app/setlist";

import { errorText, toastError, toastSuccess } from "../../../shared/toastHelper";
import InquiriesDrawer from "./InquiriesDrawer";
import SearchBar from "../shared/SearchBar";
import CountBadge from "../../shared/components/CountBadge";

const styles = {
  link: { fontWeight: 600, color: "var(--sl-text-primary)", textDecoration: "none" },
  muted: { color: "var(--sl-text-tertiary)" },
  hidden: { display: "none" },
  actions: { display: "inline-flex", alignItems: "center", gap: 8 },
  card: {
    marginTop: 24,
    overflow: "hidden",
    background: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: 12,
    boxShadow: "0px 1px 2px 0px rgba(10, 13, 18, 0.05)",
  },
  filters: { padding: "12px 24px" },
};

const ALL_VENUES = "all";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const LandingPagesIndexContainer = ({ csrfToken, team, landingPages, inquiryFields = {} }) => {
  const [pages, setPages] = useState(landingPages || []);
  const [inquiryPage, setInquiryPage] = useState(() => {
    const requestedId = Number(new URLSearchParams(window.location.search).get("inquiries"));
    return (landingPages || []).find((page) => page.id === requestedId && inquiryFields[page.template_key]) || null;
  });
  const [venue, setVenue] = useState(ALL_VENUES);
  const [search, setSearch] = useState("");
  const createForm = useRef(null);
  const basePath = `/teams/${team.id}/pages`;

  const createPage = () => createForm.current.submit();

  const copyLink = (page) => {
    navigator.clipboard.writeText(`${window.location.origin}${page.public_path}`);
    toastSuccess("Link copied to clipboard");
  };

  const archivePage = async (page) => {
    if (!window.confirm(`Archive "${page.name}"?`)) return;

    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      await axios.delete(`${basePath}/${page.id}`);
      setPages((prev) => prev.filter((item) => item.id !== page.id));
    } catch (error) {
      toastError(errorText(error, "Could not archive this page, please try again."));
    }
  };

  const venueOptions = [
    { value: ALL_VENUES, label: "All Venues" },
    ...[...new Set(pages.map((page) => page.venue_name).filter(Boolean))].sort().map((name) => ({ value: name, label: name })),
  ];

  const query = search.trim().toLowerCase();
  const visiblePages = pages.filter((page) => (
    (venue === ALL_VENUES || page.venue_name === venue)
      && (!query || page.name.toLowerCase().includes(query))
  ));

  const columns = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (page) => <a style={styles.link} href={`${basePath}/${page.id}/edit`}>{page.name}</a>,
    },
    { key: "venue", header: "Venue", render: (page) => page.venue_name || <span style={styles.muted}>No venue yet</span> },
    {
      key: "status",
      header: "Status",
      render: (page) => (
        page.status === "published"
          ? <Badge size="sm" color="success" dot>Live</Badge>
          : <Badge size="sm" color="gray" dot>Draft</Badge>
      ),
    },
    { key: "signups", header: "Signups", align: "right", render: (page) => Number(page.signups_count || 0).toLocaleString() },
    {
      key: "link",
      header: "Link",
      render: (page) => (
        page.public_path
          ? <Button variant="tertiary" size="sm" icon={Copy01} aria-label={`Copy link for ${page.name}`} onClick={() => copyLink(page)} />
          : <span style={styles.muted}>No link yet</span>
      ),
    },
    { key: "updated", header: "Updated", render: (page) => formatDate(page.updated_at) },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (page) => (
        <div style={styles.actions}>
          {inquiryFields[page.template_key] && (
            <Button variant="secondary" size="sm" onClick={() => setInquiryPage(page)}>
              {`Inquiries (${Number(page.signups_count || 0).toLocaleString()})`}
            </Button>
          )}
          <Menu
            placement="bottom-end"
            items={[{ label: "Archive", icon: Trash01, destructive: true, onClick: () => archivePage(page) }]}
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${page.name}`} />}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <ToastContainer icon={false} />
      <form ref={createForm} method="post" action={basePath} style={styles.hidden}>
        <input type="hidden" name="authenticity_token" value={csrfToken} />
      </form>

      <PageHeader
        title={(
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            Pages
            <CountBadge count={visiblePages.length} />
          </span>
        )}
        actions={(
          <>
            <Select aria-label="Venue" width={250} options={venueOptions} value={venue} onChange={setVenue} />
            <Button variant="primary" icon={Plus} onClick={createPage}>New page</Button>
          </>
        )}
      />

      <div style={styles.card}>
        <div style={styles.filters}>
          <SearchBar onDebouncedChange={setSearch} />
        </div>

        <Table
          columns={columns}
          data={visiblePages}
          getRowKey={(page) => page.id}
          empty={(
            <EmptyState
              icon={File05}
              title={pages.length ? "No pages match" : "No pages yet"}
              description={pages.length
                ? "Try a different search or venue."
                : "Pick a blueprint, add your details and publish a page that turns visitors into fans."}
              primaryAction={pages.length ? undefined : { label: "New page", icon: Plus, onClick: createPage }}
            />
          )}
        />
      </div>

      <InquiriesDrawer
        page={inquiryPage}
        fields={inquiryPage ? inquiryFields[inquiryPage.template_key] : []}
        basePath={basePath}
        onClose={() => setInquiryPage(null)}
      />
    </div>
  );
};

export default LandingPagesIndexContainer;
